import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../styles/LoginModal.module.css';
import { loginUser, newLoginUser, fetchCaptcha, verifyCaptchaThunk } from '../store/userSlice';
import { verifyPassword } from '../api/user'; // 导入密码验证API
import AlertModal from './AlertModal';
import { useTranslation } from 'react-i18next';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('code');
  const [input, setInput] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  useEffect(() => {
    if (isOpen && isCodeSent && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsCodeSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen, isCodeSent, countdown]);

  // 重置表单
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setPassword('');
    }
  }, [activeTab, isOpen]);

  if (!isOpen) return null;

  // 判断输入是邮箱还是手机号
  const isPhoneNumber = (input) => /^\d{11}$/.test(input);
  const isEmail = (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);

  const handleSendCode = () => {
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!isPhoneNumber(input) && !isEmail(input)) {
      setAlertMessage(t('login.pleaseEnterValidEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    dispatch(fetchCaptcha(input)).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        setIsCodeSent(true);
        setCountdown(60);
        const apiMessage = result.payload && result.payload.message;
        setAlertMessage(apiMessage || t('login.codeSent')); // 使用API消息或默认消息
        setAlertOpen(true);
      } else {
        setAlertMessage(t('login.failedToSendCode'));
        setAlertOpen(true);
      }
    });
  };

  // 验证码登录提交处理
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!code) {
      setAlertMessage(t('login.pleaseEnterVerificationCode'));
      setAlertOpen(true);
      return;
    }

    const captchaResult = await dispatch(verifyCaptchaThunk({ phoneNumbers: input, smsCode: code }));
    if (captchaResult.meta.requestStatus === 'fulfilled') {
      const { code: responseCode, message, uid } = captchaResult.payload;
      if (responseCode === 1) {
        const loginResult = await dispatch(newLoginUser({ uid: uid || input }));
        if (loginResult.meta.requestStatus === 'fulfilled') {
          onLoginSuccess();
        } else {
          setAlertMessage(t('login.loginFailed'));
          setAlertOpen(true);
        }
      } else {
        setAlertMessage(message);
        setAlertOpen(true);
        setCode('');
      }
    } else {
      setAlertMessage(t('login.verificationFailed'));
      setAlertOpen(true);
      setCode('');
    }
  };

  // 账号密码登录提交处理 - 修改为使用verifyPassword API
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!input) {
      setAlertMessage(t('login.pleaseEnterEmailOrPhone'));
      setAlertOpen(true);
      return;
    }
    if (!password) {
      setAlertMessage(t('login.pleaseEnterPassword'));
      setAlertOpen(true);
      return;
    }

    try {
      // 使用verifyPassword API验证密码
      const response = await verifyPassword({ 
        phoneNumbers: input, 
        password: password // 这里使用password作为smsCode参数，根据API定义
      });

      // 检查响应结果
      if (response) {
        const { code: responseCode, message, uid } = response;
        console.log('Verify Password Response:', response.data); // 打印完整的响应数据

        console.log('Verify Password Response Code:', responseCode); // 打印响应码
        console.log('Verify Password Response Message:', message); // 打印响应消息
        console.log('Verify Password Response UID:', uid); // 打印响应UID
        if (responseCode === 1) {
          // 密码验证成功，继续登录流程
          const loginResult = await dispatch(newLoginUser({ uid: uid || input }));
          if (loginResult.meta.requestStatus === 'fulfilled') {
            onLoginSuccess();
          } else {
            setAlertMessage(t('login.loginFailed'));
            setAlertOpen(true);
          }
        } else {
          // 密码验证失败
          setAlertMessage(message || t('login.incorrectPassword'));
          setAlertOpen(true);
          setPassword('');
        }
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAlertMessage(t('login.loginError'));
      setAlertOpen(true);
      setPassword('');
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
  };

  return (
    <>
      <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={styles.loginModal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <button className={styles.closeBtn} onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
            <div className={styles.logo}>
              <img
                src={`${process.env.PUBLIC_URL}/images/logo.jpg`}
                alt="Logo"
                className={styles.logoImage}
              />
              {t('header.logo')}
            </div>
            <h2>{t('login.loginRegister')}</h2>
          </div>

          <div className={styles.tabContainer}>
            <div
              className={`${styles.tab} ${activeTab === 'code' ? styles.activeTab : ''}`}
              onClick={() => switchTab('code')}
            >
              <i className="fas fa-sms"></i> {t('login.codeLogin')}
            </div>
            <div
              className={`${styles.tab} ${activeTab === 'password' ? styles.activeTab : ''}`}
              onClick={() => switchTab('password')}
            >
              <i className="fas fa-lock"></i> {t('login.passwordLogin')}
            </div>
          </div>

          <div className={styles.modalContent}>
            {/* 验证码登录表单 */}
            {activeTab === 'code' && (
              <form onSubmit={handleCodeSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="input">{t('login.account')}</label>
                  <div className={styles.inputWithIcon}>
                    <i className="fas fa-envelope"></i>
                    <input
                      type="text"
                      id="input"
                      className={styles.formControl}
                      placeholder={t('login.emailOrPhonePlaceholder')}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="verification-code">{t('login.verificationCode')}</label>
                  <div className={styles.verificationCodeContainer}>
                    <div className={styles.inputWithIcon}>
                      <i className="fas fa-key"></i>
                      <input
                        type="text"
                        id="verification-code"
                        className={styles.formControl}
                        placeholder={t('login.codePlaceholder')}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <button
                      type="button"
                      className={`${styles.sendCodeBtn} ${isCodeSent ? styles.disabled : ''}`}
                      onClick={handleSendCode}
                      disabled={isCodeSent || loading}
                    >
                      {loading ? t('login.sending') : isCodeSent ? `${countdown}${t('login.retryAfterSeconds')}` : t('login.getCode')}
                    </button>
                  </div>
                </div>
                {error && <p className={styles.error}>{error}</p>}
                <button type="submit" className={styles.btn} disabled={loading}>
                  {loading ? t('login.loggingIn') : t('login.loginRegister')}
                </button>
                <div className={styles.formFooter}>
                  <p>{t('login.firstLoginNotice')}</p>
                </div>
              </form>
            )}

            {/* 账号密码登录表单 */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="password-input">{t('login.account')}</label>
                  <div className={styles.inputWithIcon}>
                    <i className="fas fa-user"></i>
                    <input
                      type="text"
                      id="password-input"
                      className={styles.formControl}
                      placeholder={t('login.emailOrPhonePlaceholder')}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="password">{t('login.password')}</label>
                  <div className={styles.inputWithIcon}>
                    <i className="fas fa-lock"></i>
                    <input
                      type="password"
                      id="password"
                      className={styles.formControl}
                      placeholder={t('login.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
                {error && <p className={styles.error}>{error}</p>}
                <button type="submit" className={styles.btn} disabled={loading}>
                  {loading ? t('login.loggingIn') : t('login.login')}
                </button>
                <div className={styles.formFooter}>
                  <div className={styles.forgotPasswordLink}>
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      switchTab('code');
                      setAlertMessage(t('login.useCodeToResetPassword'));
                      setAlertOpen(true);
                    }}>{t('login.forgotPassword')}</a>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <AlertModal isOpen={alertOpen} message={alertMessage} onClose={handleAlertClose} />
    </>
  );
};

export default LoginModal;